import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { Loader, AlertCircle, ArrowLeft } from 'lucide-react';

export default function SurveyDisplay() {
  const { id } = useParams<{ id: string }>();
  const [survey, setSurvey] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchSurvey = async () => {
      try {
        setLoading(true);
        setError(null);
        
        console.log('Fetching survey with ID:', id);
        
        // First check if the survey exists
        const { data: surveyData, error: surveyError } = await supabase
          .from('surveys')
          .select('*')
          .eq('id', id)
          .single();
        
        if (surveyError) {
          if (surveyError.code === 'PGRST116') {
            throw new Error(`Survey with ID ${id} not found`);
          }
          throw surveyError;
        }
        
        console.log('Survey data:', surveyData);
        setSurvey(surveyData);
        
        // Now check if the survey is active
        if (!surveyData.is_active) {
          console.warn('Survey exists but is not active');
          setError('This survey is currently inactive');
        }
      } catch (err) {
        console.error('Error fetching survey:', err);
        setError(err instanceof Error ? err.message : 'Failed to load survey');
      } finally {
        setLoading(false);
      }
    };
    
    if (id) {
      fetchSurvey();
    }
  }, [id]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader className="h-12 w-12 text-[#F98B3D] animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-md mx-auto mt-8">
        <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded-md">
          <div className="flex">
            <AlertCircle className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
              <p className="text-sm text-red-700 mt-2">
                The survey with ID {id} {error.includes('not found') ? 'does not exist' : 'cannot be displayed'}.
              </p>
            </div>
          </div>
        </div>
        
        <div className="mt-4">
          <Link to="/survey" className="inline-flex items-center text-[#F98B3D] hover:text-[#e07a2c]">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to available surveys
          </Link>
        </div>
      </div>
    );
  }

  if (!survey) {
    return (
      <div className="max-w-md mx-auto mt-8">
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded-md">
          <p className="text-yellow-700">No survey data available</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto mt-8 p-6 bg-white rounded-lg shadow-md">
      <h1 className="text-2xl font-bold text-gray-900 mb-4">{survey.title}</h1>
      
      {survey.description && (
        <p className="text-gray-600 mb-6">{survey.description}</p>
      )}
      
      <div className="border-t border-gray-200 pt-4">
        <h2 className="text-lg font-medium text-gray-900 mb-2">Survey Details</h2>
        <ul className="space-y-2 text-gray-600">
          <li><strong>ID:</strong> {survey.id}</li>
          <li><strong>Status:</strong> {survey.is_active ? 'Active' : 'Inactive'}</li>
          <li><strong>Created:</strong> {new Date(survey.created_at).toLocaleString()}</li>
        </ul>
      </div>
      
      <div className="mt-8">
        <Link 
          to="/survey"
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#F98B3D] hover:bg-[#e07a2c]"
        >
          View All Surveys
        </Link>
      </div>
    </div>
  );
}